import { useMemo, useState } from "react";
import React from "react";
import { getSessionUser } from "../../utils/auth/sessionUser";
import {Segmented, Space, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import Layout from "../../components/Layout";
import PageContainer from "../../components/shared/PageContainer";
import SectionCard from "../../components/shared/SectionCard";
import EmptyState from "../../components/shared/EmptyState";
import PracDashboard from "./tabs/pracDash";
import LabDashboard from "./tabs/labDash";
import FinanceDashboard from "./tabs/financeDash";
import InventoryDashboard from "./tabs/inventoryDash";
import DashboardKPIStrip, { KPIItem } from "./components/DashboardKPIStrip";
import { hasInventoryAccess, staffCan } from "../../utils/userPrefs/validatePermissions";
import { hasFeature } from "../../utils/subscriptions/subscriptionService";
import { User } from "../../types/admin/UserTypes";

const { Title } = Typography;

type DashboardView = "practitioner" | "lab" | "finance" | "inventory";

const VIEW_ENTITLEMENTS: { key: DashboardView; label: string; can: () => boolean }[] = [
	{ key: "practitioner", label: "Practitioner", can: () => staffCan("healthcare.dashboard.read") },
	{ key: "lab",          label: "Laboratory",   can: () => hasFeature("laboratory") && staffCan("labiq.dashboard.read") },
	{ key: "finance",      label: "Finance",      can: () => staffCan("finance.dashboard.read") },
	{ key: "inventory",    label: "Inventory",    can: () => hasInventoryAccess() },
];

const DashboardPage: React.FC = () => {
	const user: User = getSessionUser();
	const queryClient = useQueryClient();
	const available = useMemo(() => VIEW_ENTITLEMENTS.filter((v) => v.can()), []);
	const [view, setView] = useState<DashboardView | null>(() => available[0]?.key ?? null);
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(30, "days"), dayjs()]);
	const [refreshToken, setRefreshToken] = useState<number>(0);
	const [kpiData, setKpiData] = useState<KPIItem[]>([]);


	return (
		<Layout>
			<PageContainer data-testid="dashboard-root">
				<SectionCard>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
						<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
							<div>
								<Title level={4}>
									{`Welcome back, ${user.first_name}`}
								</Title>
							</div>
							<DashboardKPIStrip kpis={kpiData} />
						</div>
						<Space size={12} wrap align="center">
							{available.length > 1 && (
								<Segmented
									options={available.map((v) => ({ label: v.label, value: v.key }))}
									value={view ?? undefined}
									onChange={(value) => {
										setView(value as DashboardView);
										setKpiData([]);
									}}
								/>
							)}
						</Space>
					</div>
				</SectionCard>

				{(() => {
					switch (view) {
						case "practitioner":
							return <PracDashboard startDate={dateRange[0].toDate()} endDate={dateRange[1].toDate()} refreshToken={refreshToken} onKPIChange={setKpiData} />;
						case "lab":
							return <LabDashboard startDate={dateRange[0].toDate()} endDate={dateRange[1].toDate()} refreshToken={refreshToken} onKPIChange={setKpiData} />;
						case "finance":
							return <FinanceDashboard startDate={dateRange[0].toDate()} endDate={dateRange[1].toDate()} refreshToken={refreshToken} onKPIChange={setKpiData} />;
						case "inventory":
							return <InventoryDashboard startDate={dateRange[0].toDate()} endDate={dateRange[1].toDate()} refreshToken={refreshToken} onKPIChange={setKpiData} />;
						default:
							return (
								<SectionCard>
									<EmptyState
										title="No dashboard available"
										description="Your account does not have access to any dashboard view."
									/>
								</SectionCard>
							);
					}
				})()}
			</PageContainer>
		</Layout>
	);
};

export default DashboardPage;
